import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from 'capacitor-native-biometric';

const SERVER_ID = 'com.ironforums.app';
const BIOMETRIC_ENABLED_KEY = 'biometric_enabled';

export type BiometryType = 'fingerprint' | 'face' | 'iris' | 'none';

interface BiometricAvailability {
  isAvailable: boolean;
  biometryType: BiometryType;
}

export async function isBiometricAvailable(): Promise<BiometricAvailability> {
  if (!Capacitor.isNativePlatform()) {
    return { isAvailable: false, biometryType: 'none' };
  }

  try {
    const result = await NativeBiometric.isAvailable();
    let biometryType: BiometryType = 'none';
    switch (result.biometryType) {
      case 1: biometryType = 'fingerprint'; break;
      case 2: biometryType = 'face'; break;
      case 3: biometryType = 'iris'; break;
    }
    return { isAvailable: result.isAvailable, biometryType };
  } catch {
    return { isAvailable: false, biometryType: 'none' };
  }
}

export async function enableBiometric(username: string, refreshToken: string): Promise<void> {
  await NativeBiometric.setCredentials({
    username,
    password: refreshToken,
    server: SERVER_ID,
  });
  localStorage.setItem(BIOMETRIC_ENABLED_KEY, 'true');
}

export async function disableBiometric(): Promise<void> {
  try {
    await NativeBiometric.deleteCredentials({ server: SERVER_ID });
  } catch {
    // credentials may not exist yet
  }
  localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
}

export function isBiometricEnabled(): boolean {
  return localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true';
}

export async function authenticateWithBiometric(): Promise<{ username: string; refreshToken: string }> {
  await NativeBiometric.verifyIdentity({
    reason: 'Sign in to Iron Forums',
    title: 'Biometric Login',
  });

  const credentials = await NativeBiometric.getCredentials({ server: SERVER_ID });
  return {
    username: credentials.username,
    refreshToken: credentials.password,
  };
}

export function getBiometryLabel(type: BiometryType): string {
  switch (type) {
    case 'face': return 'Face ID';
    case 'fingerprint': return 'Fingerprint';
    case 'iris': return 'Iris';
    default: return 'Biometric';
  }
}
